'use strict';

/**
 * Admin Pipeline Status showcase catalogue — mock scenario keys + display metadata.
 * Snapshots come from {@link getMockPipelineStatusSnapshotForScenario}; presentation
 * uses the same builders as the project tracker.
 */

/**
 * @typedef {'v7' | 'core' | 'discovery' | 'kits' | 'merged' | 'robustness'} ShowcaseGroupId
 */

/**
 * @typedef {Object} ShowcaseScenario
 * @property {string} id stable catalogue id
 * @property {ShowcaseGroupId} group
 * @property {string} label
 * @property {string} description
 * @property {string} [projectCode] mock project code shown like `/home/project/<code>/home`
 * @property {string | null} mockScenarioKey registry key, or null for synthetic loading/error cards
 * @property {'loading' | 'error' | 'malformed' | undefined} [synthetic]
 * @property {string} [expectedVisualState] hint for tests/docs
 */

/** @type {ReadonlyArray<{ id: ShowcaseGroupId, label: string, description: string }>} */
export const PIPELINE_STATUS_SHOWCASE_GROUPS = Object.freeze([
	{
		id: 'v7',
		label: 'V7',
		description: 'One project publishing run with manifest sample-set progress.',
	},
	{
		id: 'core',
		label: 'Global Core',
		description: 'Independent sample-set publication flows with project-level counts.',
	},
	{
		id: 'discovery',
		label: 'DiscoveryPanels',
		description: 'ProductService → PortalApi + DiscoveryPanelsApi + SmartPanelDataService (no Pathway / Heatmap / BioVis).',
	},
	{
		id: 'kits',
		label: 'Kits',
		description: 'ManifestAvailable forks to portal catalog upsert and to DataNorm publication (Received → Ready).',
	},
	{
		id: 'merged',
		label: 'Merged',
		description: 'Portal MergeProjects then ProjectAvailable (Received → Ready).',
	},
	{
		id: 'robustness',
		label: 'Loading & errors',
		description: 'Loading, API error, malformed, and unsupported states.',
	},
]);

/** @type {ReadonlyArray<ShowcaseScenario>} */
export const PIPELINE_STATUS_SHOWCASE_SCENARIOS = Object.freeze([
	{
		id: 'v7-processing',
		group: 'v7',
		label: 'Processing (multiple sample sets)',
		description: 'Current V7 run with manifest sample sets still preparing.',
		mockScenarioKey: 'v7Preparing',
		expectedVisualState: 'inProgress',
	},
	{
		id: 'v7-ready',
		group: 'v7',
		label: 'Ready',
		description: 'Current V7 publication graph nodes are all complete.',
		mockScenarioKey: 'v7Ready',
		expectedVisualState: 'ready',
	},
	{
		id: 'v7-failed',
		group: 'v7',
		label: 'Failed',
		description: 'A V7 graph node has failed.',
		mockScenarioKey: 'v7Failed',
		expectedVisualState: 'failed',
	},
	{
		id: 'v7-delayed',
		group: 'v7',
		label: 'Delayed',
		description: 'In-progress run with stale fact timestamps.',
		mockScenarioKey: 'v7Delayed',
		expectedVisualState: 'delayed',
	},
	{
		id: 'v7-empty',
		group: 'v7',
		label: 'No current publication',
		description: 'Empty telemetry — no NotStarted claim.',
		mockScenarioKey: 'v7Empty',
		expectedVisualState: 'unavailable',
	},
	{
		id: 'core-multi',
		group: 'core',
		label: 'Multiple sample sets',
		description: 'Mixed ready / publishing / preparing with summary counts.',
		mockScenarioKey: 'coreMultiSampleSet',
		expectedVisualState: 'inProgress',
	},
	{
		id: 'core-ready',
		group: 'core',
		label: 'Ready',
		description: 'All current Core sample-set flows ready (Portal + Pathway + Heatmap + BioVis complete).',
		mockScenarioKey: 'coreAllReady',
		expectedVisualState: 'ready',
	},
	{
		id: 'core-pathway',
		group: 'core',
		label: 'Processing — Pathway incomplete',
		description: 'Portal PSSA complete; Pathway still running. currentFlows stays publishing (not PSSA-only Ready).',
		mockScenarioKey: 'corePathwayIncomplete',
		expectedVisualState: 'inProgress',
	},
	{
		id: 'core-heatmap',
		group: 'core',
		label: 'Processing — Heatmap incomplete',
		description: 'Portal PSSA complete; Heatmap still running. Backend projection remains publishing.',
		mockScenarioKey: 'coreHeatmapIncomplete',
		expectedVisualState: 'inProgress',
	},
	{
		id: 'core-biovis',
		group: 'core',
		label: 'BioVis incomplete',
		description: 'BioVis started; flow stays publishing until BioVis Completed. Ready never fires while BioVis may still arrive.',
		mockScenarioKey: 'coreBioVisParticipating',
		expectedVisualState: 'inProgress',
	},
	{
		id: 'core-failed',
		group: 'core',
		label: 'Failed',
		description: 'One sample set failed; another ready.',
		mockScenarioKey: 'coreFailed',
		expectedVisualState: 'failed',
	},
	{
		id: 'core-delayed',
		group: 'core',
		label: 'Delayed',
		description: 'Preparing flow with stale startedAt.',
		mockScenarioKey: 'coreDelayed',
		expectedVisualState: 'delayed',
	},
	{
		id: 'core-empty',
		group: 'core',
		label: 'No current publication',
		description: 'Empty Core currentFlows — idle ticker plus “No current publication activity.”',
		mockScenarioKey: 'coreEmpty',
		expectedVisualState: 'unavailable',
	},
	{
		id: 'dp-processing',
		group: 'discovery',
		label: 'Processing',
		description: 'Discovery multi sample-set publishing (Portal / DiscoveryPanelsApi / SmartPanelDataService).',
		mockScenarioKey: 'discoveryPanelsOrdinaryProcessing',
		expectedVisualState: 'inProgress',
	},
	{
		id: 'dp-ready',
		group: 'discovery',
		label: 'Ready',
		description: 'Ready after Portal, Discovery Panels, SmartPanel, and visualization complete.',
		mockScenarioKey: 'discoveryPanelsSmartPanelReady',
		expectedVisualState: 'ready',
	},
	{
		id: 'dp-failed',
		group: 'discovery',
		label: 'Failed',
		description: 'Discovery sample-set failure with another ready.',
		mockScenarioKey: 'discoveryPanelsFailed',
		expectedVisualState: 'failed',
	},
	{
		id: 'dp-multi',
		group: 'discovery',
		label: 'Multiple sample sets',
		description: 'Independent Discovery sample-set flows and summary counts.',
		mockScenarioKey: 'discoveryPanelsMultiSampleSet',
		expectedVisualState: 'inProgress',
	},
	{
		id: 'dp-empty',
		group: 'discovery',
		label: 'No current publication',
		description: 'Empty Discovery currentFlows.',
		mockScenarioKey: 'discoveryPanelsEmpty',
		expectedVisualState: 'unavailable',
	},
	{
		id: 'dp-smartpanel-incomplete',
		group: 'discovery',
		label: 'SmartPanel data incomplete',
		description: 'SmartPanelDataService PSSA Started — flow stays publishing.',
		mockScenarioKey: 'discoveryPanelsSmartPanelIncomplete',
		expectedVisualState: 'inProgress',
	},
	{
		id: 'dp-smartpanel-failed',
		group: 'discovery',
		label: 'SmartPanel data failed',
		description: 'SmartPanelDataService Failed — backend failed state.',
		mockScenarioKey: 'discoveryPanelsSmartPanelFailed',
		expectedVisualState: 'failed',
	},
	{
		id: 'dp-microbiome',
		group: 'discovery',
		label: 'Microbiome',
		description: 'Representative Microbiome Discovery path (processing).',
		mockScenarioKey: 'discoveryPanelsSmartPanelMicrobiome',
		expectedVisualState: 'inProgress',
	},
	{
		id: 'dp-biological-stress',
		group: 'discovery',
		label: 'BiologicalStress',
		description: 'Representative BiologicalStress Discovery path (processing).',
		mockScenarioKey: 'discoveryPanelsSmartPanelBiologicalStress',
		expectedVisualState: 'inProgress',
	},
	{
		id: 'dp-liver-fibrosis',
		group: 'discovery',
		label: 'LiverFibrosis',
		description: 'Representative LiverFibrosis Discovery path (processing).',
		mockScenarioKey: 'discoveryPanelsSmartPanelLiverFibrosis',
		expectedVisualState: 'inProgress',
	},
	{
		id: 'dp-kidney-function',
		group: 'discovery',
		label: 'KidneyFunction',
		description: 'Representative KidneyFunction Discovery path (Ready).',
		mockScenarioKey: 'discoveryPanelsSmartPanelKidneyFunction',
		expectedVisualState: 'inProgress',
	},
	{
		id: 'kits-received',
		group: 'kits',
		label: 'Received',
		description: 'ManifestAvailable + catalog upsert; no DataNorm yet. Pizza: Received.',
		mockScenarioKey: 'kitsReceived',
		expectedVisualState: 'inProgress',
	},
	{
		id: 'kits-publishing',
		group: 'kits',
		label: 'Sample sets publishing',
		description: 'ManifestAvailable fans out to ProjectAvailable catalog upsert and to DataNorm → Portal / Pathway / BioVis. Pizza: Received → Preparing → Publishing → Ready.',
		mockScenarioKey: 'kitsPublishing',
		expectedVisualState: 'inProgress',
	},
	{
		id: 'kits-ready',
		group: 'kits',
		label: 'Ready',
		description: 'Both Kits sample sets Portal + Pathway + BioVis complete.',
		mockScenarioKey: 'kitsAllReady',
		expectedVisualState: 'ready',
	},
	{
		id: 'kits-failed',
		group: 'kits',
		label: 'Failed',
		description: 'DataNorm failed for the current sample set.',
		mockScenarioKey: 'kitsFailed',
		expectedVisualState: 'failed',
	},
	{
		id: 'kits-empty',
		group: 'kits',
		label: 'No current publication',
		description: 'Empty Kits currentFlows — idle ticker plus “No current publication activity.”',
		mockScenarioKey: 'kitsEmpty',
		expectedVisualState: 'unavailable',
	},
	{
		id: 'merged-received',
		group: 'merged',
		label: 'Received',
		description: 'MergeProjects completed; ProjectAvailable still publishing. Pizza: Received → Publishing → Ready.',
		mockScenarioKey: 'mergedReceived',
		expectedVisualState: 'inProgress',
	},
	{
		id: 'merged-ready',
		group: 'merged',
		label: 'Ready',
		description: 'MergeProjects and ProjectAvailable completed.',
		mockScenarioKey: 'mergedReady',
		expectedVisualState: 'ready',
	},
	{
		id: 'merged-failed',
		group: 'merged',
		label: 'Failed',
		description: 'MergeProjects failed; Ready stays pending.',
		mockScenarioKey: 'mergedFailed',
		expectedVisualState: 'failed',
	},
	{
		id: 'merged-empty',
		group: 'merged',
		label: 'No shell fact',
		description: 'No MergeProjects fact yet — idle Received → Publishing → Ready ticker.',
		mockScenarioKey: 'mergedNoFacts',
		expectedVisualState: 'unavailable',
	},
	{
		id: 'loading',
		group: 'robustness',
		label: 'Loading',
		description: 'Skeleton while a project tracker would load.',
		mockScenarioKey: null,
		synthetic: 'loading',
		expectedVisualState: 'loading',
	},
	{
		id: 'api-error',
		group: 'robustness',
		label: 'Mock API error',
		description: 'Error alert — does not crash the showcase.',
		mockScenarioKey: null,
		synthetic: 'error',
		expectedVisualState: 'error',
	},
	{
		id: 'malformed',
		group: 'robustness',
		label: 'Malformed response',
		description: 'Unknown statuses fail closed via existing mapper.',
		mockScenarioKey: 'v7Malformed',
		expectedVisualState: 'inProgress',
	},
	{
		id: 'unsupported',
		group: 'robustness',
		label: 'Unsupported pipeline',
		description: 'Honest unavailable for unsupported pipeline values.',
		mockScenarioKey: 'coreUnsupported',
		expectedVisualState: 'unavailable',
	},
	{
		id: 'unknown-status',
		group: 'robustness',
		label: 'Unknown status',
		description: 'Unknown wire status mapped safely.',
		mockScenarioKey: 'v7UnknownStatus',
		expectedVisualState: 'inProgress',
	},
	{
		id: 'many-sample-sets',
		group: 'robustness',
		label: 'Many sample sets',
		description: 'Large V7 manifest without layout breakage.',
		mockScenarioKey: 'v7ManySampleSets',
		expectedVisualState: 'inProgress',
	},
]);

/**
 * @param {ShowcaseScenario} scenario
 * @returns {string}
 */
export function showcaseProjectCode(scenario) {
	if (scenario.projectCode) return scenario.projectCode;
	return `DEMO-${String(scenario.id).toUpperCase().replace(/-/g, '_')}`;
}

/**
 * @param {ShowcaseGroupId} groupId
 * @returns {ShowcaseScenario[]}
 */
export function showcaseScenariosForGroup(groupId) {
	return PIPELINE_STATUS_SHOWCASE_SCENARIOS.filter((s) => s.group === groupId);
}

/**
 * @param {string} id
 * @returns {ShowcaseScenario | undefined}
 */
export function findShowcaseScenario(id) {
	return PIPELINE_STATUS_SHOWCASE_SCENARIOS.find((s) => s.id === id);
}
